import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Login      from './pages/auth/Login';
import Dashboard  from './pages/dashboard/Dashboard';
import LeadsList  from './pages/leads/LeadsList';
import LeadCreate from './pages/leads/LeadCreate';
import LeadDetail from './pages/leads/LeadDetail';
import UsersList  from './pages/users/UsersList';
import UserForm   from './pages/users/UserForm';
import RolesList  from './pages/RolesList';
import Followups  from './pages/Followups';
import Masters    from './pages/Masters';
import BsaMaster  from './pages/BsaMaster';
import Reports    from './pages/Reports';
import Incentive  from './pages/Incentive';
import PddManagement     from './pages/PddManagement';
import RevenueManagement from './pages/RevenueManagement';
import InvoiceManagement from './pages/InvoiceManagement';

import ProtectedRoute from './components/ProtectedRoute';
import authService    from './services/authService';
import { canAccessReports, canAccessFinanceAdminScreens } from './utils/roleAccess';
import { can, hasPermissionExact } from './utils/permission';

function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleForceLogout = () => {
      authService.clearAuthData();
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, [navigate]);

  return (
    <Routes>

      <Route path="/login" element={authService.isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Leads */}
      <Route path="/leads"            element={<ProtectedRoute permission="lead_read">  <LeadsList />  </ProtectedRoute>} />
      <Route path="/leads/create"     element={<ProtectedRoute permission="lead_create"><LeadCreate /> </ProtectedRoute>} />
      <Route path="/leads/:id"        element={<ProtectedRoute permission="lead_read">  <LeadDetail /> </ProtectedRoute>} />
      <Route path="/leads/:id/edit"   element={<ProtectedRoute permission="lead_update"><LeadCreate /> </ProtectedRoute>} />
      <Route path="/leads/:id/followup" element={<ProtectedRoute permission="lead_update"><LeadDetail /></ProtectedRoute>} />

      {/* Users — list/create/edit require write permission; read-only is for dropdowns only */}
      <Route path="/users"            element={<ProtectedRoute anyOf={['user_create','user_update']}><UsersList /></ProtectedRoute>} />
      <Route path="/users/create"     element={<ProtectedRoute permission="user_create"><UserForm /></ProtectedRoute>} />
      <Route path="/users/:id/edit"   element={<ProtectedRoute permission="user_update"><UserForm /></ProtectedRoute>} />

      {/* Roles */}
      <Route path="/roles"            element={<ProtectedRoute permission="role_read"><RolesList /></ProtectedRoute>} />

      {/* Followups */}
      <Route path="/followups"        element={<ProtectedRoute permission="followup_read"><Followups /></ProtectedRoute>} />

      {/* Masters — read-only is for dropdowns; page access requires write permission */}
      <Route path="/masters"          element={<ProtectedRoute anyOf={['product_create','product_update']}><Masters /></ProtectedRoute>} />
      <Route path="/bsa"              element={<ProtectedRoute anyOf={['product_create','product_update']}><BsaMaster /></ProtectedRoute>} />

      {/* Reports */}
      <Route path="/reports"          element={<ProtectedRoute check={(user) => canAccessReports(user, can)}><Reports /></ProtectedRoute>} />

      {/* Incentive */}
      <Route path="/incentive"        element={<ProtectedRoute check={(user) => canAccessFinanceAdminScreens(user, hasPermissionExact, 'incentive_read')}><Incentive /></ProtectedRoute>} />

      {/* PDD, Invoice & Revenue Management — admin-like roles only */}
      <Route path="/pdd-management"     element={<ProtectedRoute check={(user) => canAccessFinanceAdminScreens(user, hasPermissionExact, 'lead_pdd_read')}><PddManagement /></ProtectedRoute>} />
      <Route path="/invoice-management" element={<ProtectedRoute check={(user) => canAccessFinanceAdminScreens(user)}><InvoiceManagement /></ProtectedRoute>} />
      <Route path="/revenue-management" element={<ProtectedRoute check={(user) => canAccessFinanceAdminScreens(user)}><RevenueManagement /></ProtectedRoute>} />

      <Route path="/"  element={<Navigate to="/dashboard" replace />} />
      <Route path="*"  element={<Navigate to="/dashboard" replace />} />

    </Routes>
  );
}

function App() {
  return (
    <>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick draggable pauseOnHover theme="light" />
    </>
  );
}

export default App;
